import type { components } from '@/api/schema'

type Shipment = components['schemas']['PurchaseOrderDetailShipmentRead']

export interface POTrackingNumber {
  key: string
  carrier: string | null
  isPro: boolean
  number: string
  url: string | null
}

export function parseTrackingNumbers(value: string | null | undefined): string[] {
  if (!value) return []
  let parsed: unknown = value
  try {
    parsed = JSON.parse(value)
  } catch {
    // Older shipments store a plain comma-separated string.
  }
  const items = Array.isArray(parsed) ? parsed.map(String) : String(parsed).split(',')
  return items.map((item) => item.trim()).filter(Boolean)
}

/** The carrier's tracking page for one number, built the same way customer emails build it. */
function trackingUrl(provider: Shipment['shipping_provider'], number: string): string | null {
  if (!provider) return null
  if (provider.tracking_url_template) {
    return provider.tracking_url_template.replace('{tracking_number}', encodeURIComponent(number))
  }
  return provider.tracking_url ?? null
}

/** Every PRO and tracking number on the PO's live shipments, each once. */
export function poTrackingNumbers(shipments: Shipment[]): POTrackingNumber[] {
  return shipments
    .filter((shipment) => shipment.status !== 'cancelled')
    .flatMap((shipment) => {
      const carrier = shipment.carrier_name || shipment.shipping_provider?.name || null
      const pro = shipment.pro_number?.trim() || null
      const numbers = [
        ...(pro ? [{ number: pro, isPro: true }] : []),
        ...parseTrackingNumbers(shipment.tracking_numbers)
          .filter((number) => number !== pro)
          .map((number) => ({ number, isPro: false })),
      ]
      return numbers.map(({ number, isPro }) => ({
        key: `${shipment.id}-${number}`,
        carrier,
        isPro,
        number,
        url: trackingUrl(shipment.shipping_provider, number),
      }))
    })
}
